import {useEffect, useState} from 'react';
import {Box, Card, Link, Typography} from '@mui/material';
import {
  EmailBg,
  HiristDone,
  SurveyCheckGreen,
  Settings,
  BrokenLink,
  HiristInformationIcon,
} from '@jobseeker/lotus/assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import SwitchWithHeading from '@jobseeker/lotus/components/SwitchWithHeading';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {UNSUBSCRIBE_EMAIL_OPTIONS} from '../../constants';
import {useAlertCallback} from '@jobseeker/lotus/hooks/alert';
import CollectionForm from '@jobseeker/feature-profile/components/CollectionForm';
import SuccessPanel from '@jobseeker/feature-profile/components/SuccessPanel';
import unsubscribeService from '@jobseeker/common-core/services/unsubscribe.service';
import {handleTracking} from '@jobseeker/common-core/utils';
import {shallowEqual, useSelector} from 'react-redux';
import HiristBanner from '@jobseeker/lotus/components/HiristBanner';
import BannerInfo from '@jobseeker/lotus/components/BannerInfo';

import type {AppState} from '@jobseeker/common-core/types';

type UnsubscribeType = {
  isBroken: boolean;
  isMobile: boolean;
};

const UnsubscribeEmail = ({isBroken, isMobile}: UnsubscribeType) => {
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const [successInfo, setSuccessInfo] = useState({
    enabled: false,
    title: 'You have successfully subscribed!',
    subtitle: 'Thank you for subscribing to our mailing list',
  });

  const [btnText, setBtnText] = useState('');
  const [confirmCollection, setConfirmCollection] = useState(false);
  const [actionText, setActionText] = useState({key: '', title: ''});
  const [settings, setSettings] = useState<{[key: string]: number}>({});
  const [linkedEmail, setEmail] = useState('');
  const [encKey, setEncKey] = useState('');
  const [showBroken, setShowBroken] = useState('');

  const profileEmail = useSelector(
    (state: AppState) => state.user.profile.email,
    shallowEqual
  );

  const updateSettings = async (
    settings: object,
    reason: {type?: string; comment?: string; settingType?: string}
  ) => {
    const info = await unsubscribeService.saveEmailSettings(settings, reason, {
      headers: {
        Authorization: `Bearer ${encKey}`,
      },
    });

    handleTracking('Mail_Unsubscribe', {
      event_category: 'Mail_Unsubscribe',
      Source: 'email-unsubscribe',
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
      Selected_mail_group: reason?.settingType,
      Reasons: reason?.comment,
    });

    if (Object.keys(info)?.length > 0) {
      return true;
    } else {
      return false;
    }
  };

  const handleSubmit = async () => {
    if (btnText === 'Next') {
      setConfirmCollection(true);
    } else {
      const updated = await updateSettings(settings, {});
      updated &&
        setSuccessInfo({
          ...successInfo,
          enabled: true,
        });
    }
  };

  const handleCollectionForm = async (form: string, comment: string) => {
    const updated = await updateSettings(settings, {
      type: form,
      comment,
      settingType: Object.keys(settings)?.join(','),
    });

    updated &&
      setSuccessInfo({
        enabled: true,
        title: 'You have successfully unsubscribed from our mail list',
        subtitle:
          'You It may take up to 72 hours for email settings to update and reflect the changes you’ve made ',
      });
  };

  const handleResetConfimation = () => {
    setConfirmCollection(false);
  };

  const handleSyncSettingsAction = (
    name: string,
    value: number,
    action = ''
  ) => {
    setActionText({key: name, title: action});
    if (value == 1) {
      toggleConfirmPopup();
    } else {
      setSettings({
        ...settings,
        [name]: 1,
      });

      setBtnText('Save');
    }
  };

  const [ConfirmPopup, toggleConfirmPopup] = useAlertCallback({
    primaryCallback: () => {
      setSettings({
        ...settings,
        [actionText.key]: 0,
      });
      setBtnText('Next');
    },
    title: `Are you sure you want to change your ${actionText.title} preferences?`,
    subtitle: `You won't receive any further updates on recruiter activity for your applied jobs`,
    primaryAction: 'Confirm',
    secondaryAction: 'Cancel',
    useBottomSheet: true,
  });

  const getEmailSettings = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const encKey = urlParams.get('encKey');
    setEncKey(encKey ?? '');

    const {settings, email, error} = await unsubscribeService.getEmailSettings({
      headers: {
        Authorization: `Bearer ${encKey}`,
      },
    });

    if (error && error?.message) {
      setShowBroken(
        'This unsubscribe link has expired. Please open recent email from this mailing list'
      );
    } else if (isBroken && profileEmail !== email) {
      setShowBroken(
        `It looks like you're already logged in through a different account. Please log in with your email id ${email} to unsubscribe from our mail list`
      );
    } else {
      setSettings(settings);
      setEmail(email);
    }
  };

  useEffect(() => {
    if (isBroken ? profileEmail : !isBroken) {
      (async () => {
        await getEmailSettings();
      })();
    }
  }, [profileEmail]);

  return (
    <>
      {!isAppIimjobs && linkedEmail && (
        <Box mb={4}>
          <HiristBanner>
            <BannerInfo
              Icon={Settings}
              isMobile={!!isMobile}
              subTitle={`Email settings for ${linkedEmail}`}
              title={
                'You still have the option to customize your preferences to receive job recommendations that match your needs and schedule.'
              }
            />
          </HiristBanner>
        </Box>
      )}
      <Card
        sx={{
          position: 'relative',
          transition: 'all ease 0.2s',
          boxShadow: 2,
          borderRadius: isAppIimjobs ? 4 : 2,
          mx: {
            xxs: !isAppIimjobs ? 2 : 0,
            md: 14,
            lg: 24,
            xl: 32,
          },
          display: 'block',
          justifyContent: {xxs: 'center', md: 'space-between'},
          ...(!isAppIimjobs && {
            mt: {xxs: 2, md: 6},
          }),
        }}
      >
        {linkedEmail && !showBroken && (
          <>
            {isAppIimjobs && (
              <Box
                px={{xxs: 1, md: 2.4}}
                py={{xxs: 2, md: 3}}
                sx={
                  isAppIimjobs
                    ? {
                        backgroundColor: 'rgba(145, 184, 121, 0.16)',
                      }
                    : {
                        borderBottom: '1px solid #eee',
                      }
                }
              >
                <Box display={'flex'}>
                  <Image
                    src={EmailBg}
                    alt={'email_bg'}
                    width={64}
                    height={50}
                    objectFit="contain"
                    quality={100}
                  />
                  <Box ml={{xxs: 1, md: 2.5}}>
                    <Typography
                      fontSize={{xxs: 14, md: 20}}
                      fontWeight={'bold'}
                      display={{xxs: 'block', md: 'flex'}}
                    >
                      Email preference for&nbsp;
                      <Typography
                        fontSize={{xxs: 14, md: 20}}
                        fontWeight={'bold'}
                      >
                         {`“${linkedEmail}”`}
                      </Typography>
                    </Typography>
                    <Typography
                      component="p"
                      variant="subtitle3"
                      fontWeight={{xxs: 'regular', md: 'bold'}}
                      mt={0.5}
                    >
                      {`You still have the option to customize your preference to
                      receive relevant mail, that match your needs and schedule.
                      Update your preferences anytime in the`}
                      <Link
                        href={'/settings'}
                        sx={{
                          textDecoration: 'none',
                          ':hover': {
                            textDecoration: 'none',
                          },
                          cursor: 'pointer',
                        }}
                      >
                        &nbsp;Settings
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {!isAppIimjobs && (
              <Box
                display={{xxs: 'block', md: 'flex'}}
                justifyContent={{xxs: '', md: 'space-between'}}
                padding={{xxs: 1.5, md: 2}}
                borderBottom={'1px solid #eeeeee'}
                alignItems={{xxs: '', md: 'center'}}
              >
                <Typography fontSize={{xxs: 16, md: 20}} fontWeight={'bold'}>
                  Email Preference
                </Typography>
                <Typography fontSize={{xxs: 12, md: 14}}>
                  {`Anytime you can change your prefrence in`}
                  <Link
                    href={'/settings'}
                    sx={{
                      textDecoration: 'none',
                      ':hover': {
                        textDecoration: 'none',
                      },
                      cursor: 'pointer',
                    }}
                  >
                    &nbsp;Settings
                  </Link>
                  &nbsp;{`page`}
                </Typography>
              </Box>
            )}
            {!successInfo?.enabled && !confirmCollection && (
              <>
                <Box mt={isAppIimjobs ? {xxs: 1.25, md: 3} : 0}>
                  {UNSUBSCRIBE_EMAIL_OPTIONS?.map((option, index) => (
                    <Box
                      key={`setting-switch-${index}`}
                      onClick={() => {
                        handleSyncSettingsAction(
                          option.key,
                          settings[option.key],
                          option?.heading
                        );
                      }}
                    >
                      <SwitchWithHeading
                        heading={option.heading}
                        description={option.description}
                        proFeature={option.proFeature}
                        isPro={false}
                        onChange={(e: any, v: any) => {
                          console.log(e, v);
                        }}
                        value={!!settings[option.key]}
                        mainSx={
                          index === UNSUBSCRIBE_EMAIL_OPTIONS?.length - 1
                            ? {
                                borderBottom: '',
                                borderColor: '',
                              }
                            : {}
                        }
                      />
                    </Box>
                  ))}
                </Box>
                {btnText && (
                  <Button
                    sx={{
                      width: '90px',
                      background: '#333333',
                      mt: 2,
                      ml: 2,
                      mb: 2,
                      ...(!isAppIimjobs && {
                        background: 'primary',
                        color: 'primary',
                        borderRadius: '3px',
                      }),
                    }}
                    variant="contained"
                    size="large"
                    disabled={false}
                    onClick={handleSubmit}
                  >
                    {btnText}
                  </Button>
                )}
              </>
            )}

            {!successInfo?.enabled && confirmCollection && (
              <CollectionForm
                handleGoBack={handleResetConfimation}
                handleCallback={handleCollectionForm}
              />
            )}
            {successInfo?.enabled && (
              <SuccessPanel
                icon={isAppIimjobs ? SurveyCheckGreen : HiristDone}
                {...successInfo}
              >
                {btnText === 'Next' && (
                  <Typography variant="subtitle3" color="text.secondary">
                    If you would like to re-subscribe, please
                    <Link
                      href={'/settings'}
                      sx={{
                        textDecoration: 'none',
                        ':hover': {
                          textDecoration: 'none',
                        },
                        cursor: 'pointer',
                      }}
                    >
                      &nbsp;Re-subscribe
                    </Link>
                  </Typography>
                )}
              </SuccessPanel>
            )}
          </>
        )}

        {!!showBroken && (
          <SuccessPanel
            title={isBroken ? showBroken : 'Unsubscribe link expired'}
            subtitle={
              isBroken
                ? ''
                : 'This unsubscribe link has expired. Please open recent email from this mailing list'
            }
            icon={isAppIimjobs ? BrokenLink : HiristInformationIcon}
          >
            <Button
              href="/"
              target="_self"
              variant="contained"
              sx={{
                width: {xxs: '100%', md: 'fit-content'},
                background: '#000',
                mt: {xxs: 1, md: 2},
                ...(!isAppIimjobs && {
                  background: 'primary',
                  color: 'primary',
                  borderRadius: '3px',
                }),
              }}
            >
              Go Home
            </Button>
          </SuccessPanel>
        )}
      </Card>

      <ConfirmPopup />
    </>
  );
};

export default UnsubscribeEmail;
